import { ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, Output, computed, signal } from '@angular/core';
import { NgClass } from '@angular/common';
import { ApiClientConstant, Table } from '@cureskin/api-client';
import { RequestCallBannerComponent } from '@shared/pending-call-banners/request-call-banner/request-call-banner.component';
import { UserQueryChatBannerComponent } from '@shared/user-query-chat-banner/user-query-chat-banner.component';
import { CallScheduledBannerComponent } from '@shared/pending-call-banners/call-scheduled-banner/call-scheduled-banner.component';
import { CallCompletedBannerComponent } from '@shared/pending-call-banners/call-completed-banner/call-completed-banner.component';
import {
  DoctorCallUnsuccessfulBannerComponent
} from '@shared/pending-call-banners/doctor-call-unsuccessful-banner/doctor-call-unsuccessful-banner.component';
import { UnpaidPendingCallService } from '@services/pending-call-banner-service';
import { PendingCall, UnpaidUserDoctorCallVariant} from '@services/pending-call-banner-service/types';
import { Regimen } from '../../app-constant-types';
import { Experiment } from '@cureskin/api-client/src/server-api';
import { AppConfig } from '../../app.config';
import { RegimenService } from '@services/regimen-service';
import { RegimenInstructionBannerComponent } from '@shared/regimen-instruction-banner/regimen-instruction-banner.component';
import { UserService } from '@services/user';

@Component({
  selector: 'support-unpaid-doctor-call-widget',
  imports: [
    RequestCallBannerComponent,
    UserQueryChatBannerComponent,
    CallScheduledBannerComponent,
    CallCompletedBannerComponent,
    DoctorCallUnsuccessfulBannerComponent,
    NgClass,
    RegimenInstructionBannerComponent
],
  templateUrl: './support-unpaid-doctor-call-widget.component.html',
})
export class SupportUnpaidDoctorCallWidgetComponent implements OnChanges {
  @Input() user: Table.User;
  @Input() regimen: Regimen;
  @Input() experiments: Experiment[];
  @Input() supportView: boolean;
  @Output() showDrCallBannerToggle = new EventEmitter<boolean>();
  @Output() showLlmChatBannerToggle = new EventEmitter<boolean>();
  shouldShowPendingCallBanner: boolean = false;
  shouldShowUnpaidLlmChatBanner: boolean = false;
  pendingCallBannerExperimentVariantData: UnpaidUserDoctorCallVariant;
  pendingCallState: string | null;
  hidePendingCallBanner: boolean = false;
  isPaidUser: boolean;
  bannerCheckComplete: boolean;
  unpaidUserDoctorCalls: PendingCall[];
  newBannerUI: boolean = false;
  regimenKitImage: string = '';
  regimenBackgroundColor: string;
  private readonly instructionBannerVariant = signal<any>(null);
  readonly showInstructionBanner = computed<boolean>(() => {
    const variant = this.instructionBannerVariant();
    if (!variant) return false;
    if (!this.userService.isUnpaidUser(this.user)) return false;
    if (this.regimenService.isAnyRegimenPreparationOngoing()) return false;
    return !!variant.help_instruction_banner;
  });
  protected readonly ApiClientConstant = ApiClientConstant;

  constructor(
    protected readonly appConfig: AppConfig,
    private readonly changeDetector: ChangeDetectorRef,
    private readonly unpaidPendingCallService: UnpaidPendingCallService,
    private readonly regimenService: RegimenService,
    private readonly userService: UserService,
  ) {
  }

  async ngOnChanges(): Promise<void> {
    if (this.user && this.regimen && this.experiments) {
      await this.initializeComponent();
    }
  }

  private async initializeComponent(): Promise<void> {
    this.findActiveExperiments(this.experiments);
    this.updateBannerRegimenAssets();
  }

  private updateBannerRegimenAssets(): void {
    const regimen = this.displayRegimenForBanner;
    this.regimenKitImage = regimen?.regimenCoverImage ?? '';
    const variantId = this.regimenService.getSelectedVariantIdFromRegimen(regimen);
    this.regimenBackgroundColor = this.regimenService.getBackgroungColourBasedOnVariantId(variantId);
  }

  get userLanguagePreference(): string {
    return this.user?.get('languagePreference');
  }

  get isUserPaid(): boolean {
    return !this.userService.hasNotPlacedOrder(this.user);
  }

  get regimenObjectId(): string {
    return this.regimen.objectId;
  }

  get regimenClass(): string {
    return this.regimen.class;
  }

  get displayRegimenForBanner(): any {
    const displayClass = this.regimenService.getDisplayRegimenClass();
    if (!displayClass) return this.userRegimen;
    const fromRegimens = this.regimenService.regimens().find((r: any) => r?.class === displayClass);
    return fromRegimens ?? this.userRegimen;
  }

  get userRegimen() {
    return this.regimen as any;
  }

  getContainerClassView(): boolean {
    return !this.shouldShowPendingCallBanner && (this.shouldShowUnpaidLlmChatBanner && ![
      this.appConfig.Shared.Languages.EN,
      this.appConfig.Shared.Languages.HI,
      ].includes(this.userLanguagePreference)) || this.isUserPaid || this.newBannerUI;
  }

  findActiveExperiments(experiments: Experiment[]): void {
    experiments.forEach(async (exp: any): Promise<void> => {
      if (exp.key === 'unpaid_user_llm_bot') this.checkForLlmChatBanner(exp.variant);
      if (exp.key === 'unpaid_user_pending_call_banner') await this.checkForPendingCallBanner(exp.variant);
      if (exp.key === 'instruction_visibility_improvement') this.checkInstructionBanner(exp.variant);
    });
  }

  isAnyRegimenPreparationTimePending(regimen: Regimen): boolean {
    return !!regimen.isPreparationTimePending === true;
  }

  checkForLlmChatBanner(variant): void {
    if (!variant) return;

    const isLanguageSupported: boolean = variant.languages.includes(this.user?.get('languagePreference'));
    const showInRegimenPage: boolean = variant.pagesToShow.includes(this.appConfig.Shared.PAGES.SUPPORT);
    this.shouldShowUnpaidLlmChatBanner = !this.isUserPaid &&
      !this.isAnyRegimenPreparationTimePending(this.userRegimen) &&
      isLanguageSupported &&
      showInRegimenPage;
    this.showLlmChatBannerToggle.emit(this.shouldShowUnpaidLlmChatBanner);
  }

  async checkForPendingCallBanner(variant): Promise<void> {
    this.unpaidUserDoctorCalls = await this.unpaidPendingCallService.getFilteredPendingCalls(this.regimenObjectId)
    this.pendingCallBannerExperimentVariantData = variant;
    this.newBannerUI = variant.newBannerUI

    this.shouldShowPendingCallBanner = !this.isUserPaid
      && !this.isAnyRegimenPreparationTimePending(this.userRegimen)
      && !this.hidePendingCallBanner;

    this.showDrCallBannerToggle.emit(this.shouldShowPendingCallBanner);

    const { status, hideBanner }: {
      status: string | null,
      hideBanner: boolean,
    } = await this.unpaidPendingCallService.determinePendingCallState(this.unpaidUserDoctorCalls);

    this.pendingCallState = status;
    this.hidePendingCallBanner = hideBanner;
    this.bannerCheckComplete = true;

    this.changeDetector.markForCheck();
  }

  checkInstructionBanner(variant): void {
    this.instructionBannerVariant.set(variant);
  }

  async onPendingCallCreated(): Promise<void> {
    this.hidePendingCallBanner = false;
    this.changeDetector.markForCheck();
    await this.checkForPendingCallBanner(this.pendingCallBannerExperimentVariantData);
  }

  updateBackgroundColor(): void {
    this.updateBannerRegimenAssets();
  }
}
